import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ROUTES from '../routes/routeConstants';
import {
  ScenarioControllerApi,
  LocationControllerApi,
  YearPlanControllerApi,
  PatientControllerApi
} from '../api/generated';
import type { YearPlanResponse, WeekDateRangeResponse } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setPatients, selectPatientsById } from '../store/patients/patientsSlice';
import { showError } from '../store/errorDialog/errorDialogSlice';
import type { EconomicsLeafState, EconomicsTreeModel } from '../types/economicsTypes';
import { buildLeafStates, buildTree, diffChanges } from '../services/economicsTree';
import { saveEconomicsBatch } from '../services/economicsService';

interface WeekSpan {
  from: number;
  to: number;
}

function weekSpan(weeks: WeekDateRangeResponse[]): WeekSpan {
  if (weeks.length === 0) return { from: 0, to: 0 };
  const numbers = weeks.map((w) => w.weekNumber);
  return { from: Math.min(...numbers), to: Math.max(...numbers) };
}

function cloneLeaves(leaves: Map<string, EconomicsLeafState>): Map<string, EconomicsLeafState> {
  return new Map([...leaves].map(([key, state]) => [key, { ...state }]));
}

export function useEconomics(locationId: string | undefined) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const patientsById = useAppSelector(selectPatientsById);

  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const scenarioYearRef = useRef<number>(new Date().getFullYear());
  const [scenarioId, setScenarioId] = useState<string | null>(null);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [yearPlan, setYearPlan] = useState<YearPlanResponse | null>(null);
  const [working, setWorking] = useState<Map<string, EconomicsLeafState>>(new Map());
  const [fromWeek, setFromWeek] = useState(0);
  const [toWeek, setToWeek] = useState(0);

  // Server snapshot, derived from the loaded year plan — the baseline the working copy diffs against.
  const serverLeaves = useMemo(
    () => (yearPlan ? buildLeafStates(yearPlan) : new Map<string, EconomicsLeafState>()),
    [yearPlan]
  );

  // Applies a fresh server year plan: rebuilds the working copy, optionally resetting the selected
  // period to the full span (on first load / year change, not on a post-save re-fetch).
  const applyServerYearPlan = useCallback((plan: YearPlanResponse, resetPeriod: boolean) => {
    setYearPlan(plan);
    setWorking(buildLeafStates(plan));
    if (resetPeriod) {
      const span = weekSpan(plan.weeks);
      setFromWeek(span.from);
      setToWeek(span.to);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (!locationId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const config = createApiConfiguration();
        const scenarioApi = new ScenarioControllerApi(config);
        const locationApi = new LocationControllerApi(config);
        const yearPlanApi = new YearPlanControllerApi(config);
        const patientApi = new PatientControllerApi(config);

        const [scenarioResponse, locationsResponse] = await Promise.all([
          scenarioApi.getCurrentScenario({ locationId }),
          locationApi.getLocations()
        ]);

        const fetchedScenarioId = scenarioResponse.data.id;
        setScenarioId(fetchedScenarioId ?? null);
        scenarioYearRef.current = scenarioResponse.data.year;
        setSelectedYear(scenarioResponse.data.year);

        if (fetchedScenarioId) {
          const [yearPlanResponse, patientsResponse] = await Promise.all([
            yearPlanApi.getYearPlan({ locationId, scenarioId: fetchedScenarioId }),
            patientApi.listPatients({ locationId })
          ]);
          applyServerYearPlan(yearPlanResponse.data, true);
          dispatch(setPatients(patientsResponse.data));
        }

        const match = locationsResponse.data.find((l) => l.id === locationId);
        setLocationName(match?.name ?? null);
      } catch (error) {
        console.error('Failed to load economics on initial fetch:', error);
        navigate(`/${ROUTES.errorPage}`);
      } finally {
        setLoading(false);
      }
    };
    void fetchData();
  }, [locationId, applyServerYearPlan, dispatch, navigate]);

  // Year change
  useEffect(() => {
    if (!locationId || selectedYear === scenarioYearRef.current) return;
    const fetchYearPlan = async () => {
      setLoading(true);
      try {
        const config = createApiConfiguration();
        const scenarioApi = new ScenarioControllerApi(config);
        const yearPlanApi = new YearPlanControllerApi(config);
        const patientApi = new PatientControllerApi(config);

        const scenarioResponse = await scenarioApi.getScenarioForYear({ locationId, year: selectedYear });
        const fetchedScenarioId = scenarioResponse.data.id;
        setScenarioId(fetchedScenarioId ?? null);
        scenarioYearRef.current = scenarioResponse.data.year;

        if (fetchedScenarioId) {
          const [yearPlanResponse, patientsResponse] = await Promise.all([
            yearPlanApi.getYearPlan({ locationId, scenarioId: fetchedScenarioId }),
            patientApi.listPatients({ locationId })
          ]);
          applyServerYearPlan(yearPlanResponse.data, true);
          dispatch(setPatients(patientsResponse.data));
        } else {
          setYearPlan(null);
          setWorking(new Map());
        }
      } catch (error) {
        console.error('Failed to load economics on year change:', error);
        dispatch(showError({ message: t('fetch-error') }));
        setSelectedYear(scenarioYearRef.current);
      } finally {
        setLoading(false);
      }
    };
    void fetchYearPlan();
  }, [locationId, selectedYear, applyServerYearPlan, dispatch, t]);

  const getPatientName = useCallback(
    (patientId: string) => patientsById[patientId]?.name ?? t('economics-unknown-patient'),
    [patientsById, t]
  );

  const tree: EconomicsTreeModel = useMemo(() => {
    if (!yearPlan) return { plans: [], grandTotal: 0, completedCount: 0 };
    return buildTree(yearPlan, working, fromWeek, toWeek, getPatientName);
  }, [yearPlan, working, fromWeek, toWeek, getPatientName]);

  const changes = useMemo(() => diffChanges(serverLeaves, working), [serverLeaves, working]);
  const isDirty = changes.length > 0;

  const setPeriod = useCallback((from: number, to: number) => {
    const lo = Math.min(from, to);
    const hi = Math.max(from, to);
    setFromWeek(lo);
    setToWeek(hi);
  }, []);

  // Sets completion across a set of leaves. Uncompleting also clears the override (mirrors the backend).
  const toggleKeys = useCallback((keys: string[], completed: boolean) => {
    setWorking((prev) => {
      const next = new Map(prev);
      for (const key of keys) {
        const current = next.get(key);
        const keepsOverride = completed && current?.reimbursementOverride != null;
        next.set(key, {
          completed,
          reimbursementOverride: keepsOverride ? (current?.reimbursementOverride ?? null) : null,
          note: keepsOverride ? (current?.note ?? null) : null
        });
      }
      return next;
    });
  }, []);

  const toggleLeaf = useCallback(
    (key: string, completed: boolean) => toggleKeys([key], completed),
    [toggleKeys]
  );

  // The note is mandatory whenever `value` differs from what the server has — the dialog enforces
  // that before calling this, and the backend rejects the batch if it somehow gets through.
  const setOverride = useCallback((key: string, value: number | null, note: string | null) => {
    setWorking((prev) => {
      const current = prev.get(key);
      if (!current) return prev;
      const next = new Map(prev);
      next.set(key, { completed: current.completed, reimbursementOverride: value, note });
      return next;
    });
  }, []);

  const discard = useCallback(() => {
    setWorking(cloneLeaves(serverLeaves));
  }, [serverLeaves]);

  const save = useCallback(async () => {
    if (!locationId || !scenarioId || changes.length === 0) return;
    setSaving(true);
    try {
      await saveEconomicsBatch(locationId, scenarioId, changes);
      const config = createApiConfiguration();
      const yearPlanApi = new YearPlanControllerApi(config);
      const refreshed = await yearPlanApi.getYearPlan({ locationId, scenarioId });
      applyServerYearPlan(refreshed.data, false);
    } catch (error) {
      const isConflict = axios.isAxiosError(error) && error.response?.status === 409;
      dispatch(showError({ message: t(isConflict ? 'economics-save-conflict' : 'economics-save-error') }));
    } finally {
      setSaving(false);
    }
  }, [locationId, scenarioId, changes, applyServerYearPlan, dispatch, t]);

  return {
    yearPlan,
    selectedYear,
    setSelectedYear,
    scenarioId,
    locationName,
    loading,
    saving,
    weeks: yearPlan?.weeks ?? [],
    fromWeek,
    toWeek,
    setPeriod,
    tree,
    isDirty,
    toggleLeaf,
    toggleKeys,
    setOverride,
    save,
    discard
  };
}
